"use client";

import * as React from "react";
import { ChevronsUpDownIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * Shared internals for the searchable Popover + Command selectors.
 *
 * Both {@link MultiSelect} (multi-value, badge display) and {@link Combobox}
 * (single-value, label display) build on this core. The only things that
 * differ between them are the selection model (a `Set` vs a single value) and
 * the selected-value display component, both injected by the root component.
 *
 * This module is internal and intentionally not exported from the package
 * index — consumers use the `MultiSelect*` / `Combobox*` public components.
 */

/** A selected option with its registered display label, passed to `format` render-props. */
export type SelectedItem = { value: string; label: React.ReactNode };

export type SelectCoreContextValue = {
	open: boolean;
	setOpen: (open: boolean) => void;
	/** Whether multiple values may be selected. Single-select hides the item checkbox until selected. */
	multiple: boolean;
	/** Ordered selected values: 0..1 for single-select, 0..n for multi-select. */
	selectedValues: string[];
	isSelected: (value: string) => boolean;
	/** Multi-select toggles (and stays open); single-select sets the value and closes. */
	toggle: (value: string) => void;
	items: Map<string, React.ReactNode>;
	onItemAdded: (value: string, label: React.ReactNode) => void;
	collectLabels: (children: React.ReactNode) => void;
};

const SelectCoreContext = React.createContext<SelectCoreContextValue | null>(
	null,
);

export function useSelectCoreContext(): SelectCoreContextValue {
	const context = React.useContext(SelectCoreContext);
	if (context == null) {
		throw new Error(
			"Select sub-components must be used within a MultiSelect or Combobox",
		);
	}
	return context;
}

type SelectCoreProviderProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	/** Whether multiple values may be selected. Single-select hides the item checkbox until selected. */
	multiple: boolean;
	selectedValues: string[];
	isSelected: (value: string) => boolean;
	toggle: (value: string) => void;
	/**
	 * Root-specific walker that registers labels for its own item component type.
	 * Must be a stable reference — both roots bind theirs at module scope — since
	 * it keys the memoised `collectLabels` the content panel walks children with.
	 */
	collectItemLabels: (
		children: React.ReactNode,
		register: (value: string, label: React.ReactNode) => void,
	) => void;
	children: React.ReactNode;
};

/**
 * Provides the selection context and owns the item-label `Map`. The root
 * component supplies the selection model (`selectedValues`/`isSelected`/`toggle`)
 * and a walker that knows how to read its own item component's label override.
 */
export function SelectCoreProvider({
	open,
	setOpen,
	multiple,
	selectedValues,
	isSelected,
	toggle,
	collectItemLabels,
	children,
}: SelectCoreProviderProps) {
	// State rather than a mutable ref: the map is read while rendering the
	// trigger's selected-value display, and a registration has to reach that
	// read through a re-render rather than by mutating an object in place.
	const [items, setItems] = React.useState<Map<string, React.ReactNode>>(
		() => new Map(),
	);

	const onItemAdded = React.useCallback(
		(value: string, label: React.ReactNode) => {
			setItems((prev) => {
				if (prev.has(value) && prev.get(value) === label) {
					return prev;
				}
				const next = new Map(prev);
				next.set(value, label);
				return next;
			});
		},
		[],
	);

	const collectLabels = React.useCallback(
		(nodes: React.ReactNode) => {
			collectItemLabels(nodes, onItemAdded);
		},
		[collectItemLabels, onItemAdded],
	);

	const contextValue = React.useMemo(
		() => ({
			open,
			setOpen,
			multiple,
			selectedValues,
			isSelected,
			toggle,
			items,
			onItemAdded,
			collectLabels,
		}),
		[
			open,
			setOpen,
			multiple,
			selectedValues,
			isSelected,
			toggle,
			items,
			onItemAdded,
			collectLabels,
		],
	);

	return (
		<SelectCoreContext value={contextValue}>
			<Popover open={open} onOpenChange={setOpen} modal={true}>
				{children}
			</Popover>
		</SelectCoreContext>
	);
}
SelectCoreProvider.displayName = "SelectCoreProvider";

/** Props shared by the multi-select and combobox triggers. */
export type SelectCoreTriggerProps = {
	className?: string;
	children?: React.ReactNode;
	/** Marks the trigger as invalid (adds a red border). */
	isInvalid?: boolean;
} & React.ComponentPropsWithoutRef<typeof Button>;

/** Trigger button that opens the popover. Renders as a combobox. */
export const SelectCoreTrigger = React.forwardRef<
	HTMLButtonElement,
	SelectCoreTriggerProps
>(({ className, children, isInvalid = false, ...props }, ref) => {
	const { open } = useSelectCoreContext();

	return (
		<PopoverTrigger asChild>
			<Button
				ref={ref}
				{...props}
				variant={props.variant ?? "outline"}
				role={props.role ?? "combobox"}
				aria-expanded={props["aria-expanded"] ?? open}
				aria-invalid={props["aria-invalid"] ?? isInvalid}
				className={cn(
					"flex h-auto min-h-9 w-fit items-center justify-between gap-2 overflow-hidden rounded-md border border-input bg-combobox-trigger px-3 py-1.5 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-neutral-950 focus-visible:ring-[3px] focus-visible:ring-neutral-950/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-red-500 aria-invalid:ring-red-500/20 data-[placeholder]:text-neutral-500 dark:bg-neutral-800/30 dark:hover:bg-neutral-800/50 dark:aria-invalid:ring-red-600/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-neutral-500 dark:border-neutral-800 dark:focus-visible:border-neutral-300 dark:focus-visible:ring-neutral-300/50 dark:aria-invalid:border-red-600 dark:aria-invalid:ring-red-600/20 dark:data-[placeholder]:text-neutral-400 dark:[&_svg:not([class*='text-'])]:text-neutral-400",
					isInvalid && "border-danger-400",
					className,
				)}
			>
				{children}
				<ChevronsUpDownIcon className="size-4 shrink-0 opacity-50" />
			</Button>
		</PopoverTrigger>
	);
});
SelectCoreTrigger.displayName = "SelectCoreTrigger";

/** Props shared by the multi-select and combobox content panels. */
export type SelectCoreContentProps = {
	/**
	 * Controls the search input. Pass `true` to enable with defaults,
	 * `false` to disable, or an object with `placeholder` and `emptyMessage`.
	 */
	search?: boolean | { placeholder?: string; emptyMessage?: string };
	children: React.ReactNode;
	/**
	 * Callback fired when the search input value changes. Also called with an
	 * empty string when the popover closes, so consumers can reset any external
	 * search state they keep in sync.
	 */
	onValueChange?: (search: string) => void;
	/**
	 * Optional className applied to the `PopoverContent` panel (e.g. to widen it).
	 * Defaults to `undefined`, leaving the panel width unchanged.
	 */
	contentClassName?: string;
	/**
	 * Optional inline style applied to the `PopoverContent` panel (e.g. to set a
	 * dynamic `minWidth`). Defaults to `undefined`.
	 */
	contentStyle?: React.CSSProperties;
	/**
	 * Optional content rendered inside the `Command` after the scrollable list
	 * (e.g. a "Clear selection" action). Stays fixed below the list and is
	 * unaffected by search filtering.
	 */
	footer?: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<typeof Command>, "children">;

/** Popover content containing a searchable list of options. */
export function SelectCoreContent({
	search = true,
	children,
	onValueChange,
	contentClassName,
	contentStyle,
	footer,
	...props
}: SelectCoreContentProps) {
	const { collectLabels, open } = useSelectCoreContext();
	const canSearch = typeof search === "object" ? true : search;
	const stealFocusRef = React.useCallback((node: HTMLButtonElement | null) => {
		node?.focus();
	}, []);

	// PopoverContent unmounts when closed, so item effects never run until the
	// user opens the popover. Eagerly walk the element tree here (which is
	// always rendered) to pre-populate the label map.
	React.useLayoutEffect(() => {
		collectLabels(children);
	}, [children, collectLabels]);

	// Reset any externally-tracked search state when the popover closes. The ref
	// keeps `onValueChange` out of the dependencies below, so an inline callback's
	// changing identity doesn't re-run the transition check on every render while
	// closed. It publishes on commit, and declaration order puts that ahead of the
	// reader in the same commit. (`useEffectEvent` says this in one line, but it
	// is React 19.2 and this package's peer range is `^19.0`.)
	const onValueChangeRef = React.useRef(onValueChange);
	React.useEffect(() => {
		onValueChangeRef.current = onValueChange;
	});

	// Only on the open -> closed transition, never on mount.
	const wasOpenRef = React.useRef(open);
	React.useEffect(() => {
		if (wasOpenRef.current && !open) {
			onValueChangeRef.current?.("");
		}
		wasOpenRef.current = open;
	}, [open]);

	return (
		<PopoverContent
			data-testid="combobox-content"
			className={cn(
				"min-w-[var(--radix-popover-trigger-width)] p-0",
				contentClassName,
			)}
			style={contentStyle}
		>
			<Command {...props}>
				{canSearch ? (
					<CommandInput
						placeholder={
							typeof search === "object" ? search.placeholder : undefined
						}
						onValueChange={onValueChange}
					/>
				) : (
					<button
						ref={stealFocusRef}
						type="button"
						aria-hidden="true"
						tabIndex={-1}
						className="sr-only"
					/>
				)}
				<CommandList className="p-1">
					{canSearch && (
						<CommandEmpty>
							{typeof search === "object" ? search.emptyMessage : undefined}
						</CommandEmpty>
					)}
					{children}
				</CommandList>
				{footer}
			</Command>
		</PopoverContent>
	);
}
SelectCoreContent.displayName = "SelectCoreContent";

/** Props for the shared {@link SelectCoreItem}. */
export type SelectCoreItemProps = {
	/** The string value that identifies this option. */
	value: string;
	/** Override the label registered for the trigger display. Falls back to `children`. */
	label?: React.ReactNode;
	/** Muted subtext rendered beneath `children`. Never included in the trigger display. */
	description?: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<typeof CommandItem>, "value">;

/** A single selectable option rendered with a presentational checkbox indicator. */
export function SelectCoreItem({
	value,
	children,
	label,
	description,
	onSelect,
	className,
	...props
}: SelectCoreItemProps) {
	const { toggle, isSelected, onItemAdded, multiple } = useSelectCoreContext();
	const selected = isSelected(value);

	React.useEffect(() => {
		onItemAdded(value, label ?? children);
	}, [value, children, onItemAdded, label]);

	return (
		<CommandItem
			{...props}
			className={cn(description != null && "items-start", className)}
			onSelect={() => {
				toggle(value);
				onSelect?.(value);
			}}
		>
			<Checkbox
				checked={selected}
				className={cn(
					"pointer-events-none mr-3 h-4 w-4",
					description != null && "mt-0.5",
					!multiple && "data-[state=unchecked]:opacity-0",
				)}
				aria-hidden
				tabIndex={-1}
			/>
			{description != null ? (
				<div className="flex min-w-0 flex-col gap-0.5">
					<span>{children}</span>
					<span className="text-xs text-neutral-500">{description}</span>
				</div>
			) : (
				children
			)}
		</CommandItem>
	);
}
SelectCoreItem.displayName = "SelectCoreItem";

/** Visually groups related items under an optional heading. */
export function SelectCoreGroup(
	props: React.ComponentPropsWithoutRef<typeof CommandGroup>,
) {
	return <CommandGroup {...props} />;
}
SelectCoreGroup.displayName = "SelectCoreGroup";

/** Horizontal rule between groups of items. */
export function SelectCoreSeparator(
	props: React.ComponentPropsWithoutRef<typeof CommandSeparator>,
) {
	return <CommandSeparator {...props} />;
}
SelectCoreSeparator.displayName = "SelectCoreSeparator";

/**
 * Builds a recursive children walker that registers labels for items of the
 * given component type. Each root binds this to its own item component and the
 * prop that overrides the display label (`badgeLabel` for multi-select, `label`
 * for combobox).
 */
export function makeCollectItemLabels(
	itemType: React.ElementType,
	getOverride: (props: Record<string, unknown>) => React.ReactNode,
): (
	children: React.ReactNode,
	register: (value: string, label: React.ReactNode) => void,
) => void {
	function collect(
		children: React.ReactNode,
		register: (value: string, label: React.ReactNode) => void,
	): void {
		React.Children.forEach(children, (child) => {
			if (!React.isValidElement(child)) {
				return;
			}
			const props = child.props as Record<string, unknown>;
			if (child.type === itemType) {
				register(
					props.value as string,
					getOverride(props) ?? (props.children as React.ReactNode),
				);
			}
			if (props && typeof props === "object" && "children" in props) {
				collect(props.children as React.ReactNode, register);
			}
		});
	}
	return collect;
}

/**
 * Resolves the controlled/uncontrolled selection model shared by {@link Combobox}
 * and {@link MultiSelect}.
 *
 * Controlled-ness is determined by the caller from prop *presence* (e.g.
 * `"value" in props`), not from the prop's value, and locked on the first
 * render. Deriving it from the value (`controlled !== undefined`) would both
 * (a) lock a consumer that is controlled but starts empty/undefined into
 * uncontrolled mode forever, and (b) flip a controlled component to uncontrolled
 * the moment its value is cleared to `undefined`, falling back to stale internal
 * state. Keying off presence avoids both. This mirrors Radix's
 * `useControllableState` contract.
 *
 * @param controlledOnMount - Whether the controlling prop was present at mount.
 * @returns `[value, setValue]` where `value` reflects the controlled prop when
 * controlled (or internal state otherwise), and `setValue` updates internal
 * state only in uncontrolled mode (a no-op when controlled — the parent owns it).
 */
export function useControllableState<T>(
	controlledOnMount: boolean,
	controlled: T | undefined,
	defaultValue: T | undefined,
): readonly [T | undefined, (next: T) => void] {
	const [isControlled] = React.useState(controlledOnMount);
	const [internal, setInternal] = React.useState<T | undefined>(
		controlled ?? defaultValue,
	);
	const value = isControlled ? controlled : internal;
	const setValue = React.useCallback(
		(next: T) => {
			if (!isControlled) {
				setInternal(next);
			}
		},
		[isControlled],
	);
	return [value, setValue];
}
